import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const WelcomeCard = ({
  onStartTour,
  onSkipTour,
  onImportSampleTasks,
  sampleTasksImported,
  isImporting,
}) => {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="bg-card rounded-2xl border-2 border-border shadow-soft-xl p-6 md:p-8 lg:p-10 mb-6">
        <div className="text-center mb-8">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Icon name="Sparkles" size={40} color="var(--color-primary)" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Welcome to Eisenhower Matrix
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Master the art of prioritization with the proven Eisenhower Matrix methodology.
            Let's take a quick tour to get you started!
          </p>
        </div>

        {/* Feature Highlights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="bg-background rounded-xl p-5 border border-border">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
              <Icon name="Grid3x3" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              4-Quadrant System
            </h3>
            <p className="text-sm text-muted-foreground">
              Organize tasks by urgency and importance for clear prioritization
            </p>
          </div>

          <div className="bg-background rounded-xl p-5 border border-border">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
              <Icon name="Move" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Drag & Drop
            </h3>
            <p className="text-sm text-muted-foreground">
              Easily move tasks between quadrants as priorities change
            </p>
          </div>

          <div className="bg-background rounded-xl p-5 border border-border">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
              <Icon name="Filter" size={24} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Smart Filters
            </h3>
            <p className="text-sm text-muted-foreground">
              Search, sort, and filter tasks to find what matters most
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
          <Button
            variant="default"
            size="lg"
            onClick={onStartTour}
            iconName="Play"
            className="w-full sm:w-auto"
          >
            Start Interactive Tour
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={onSkipTour}
            className="w-full sm:w-auto"
          >
            Skip to Dashboard
          </Button>
        </div>
      </div>

      {/* Sample Tasks Section */}
      <div className="bg-card rounded-2xl border border-border shadow-soft-lg p-6 md:p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Download" size={24} color="var(--color-success)" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Import Sample Tasks
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-4">
              Get started quickly with pre-configured example tasks. See how different types of tasks
              are organized across the four quadrants.
            </p>
            {sampleTasksImported ? (
              <div className="flex items-center gap-2 text-success">
                <Icon name="CheckCircle" size={20} color="currentColor" />
                <span className="text-sm font-medium">Sample tasks imported successfully!</span>
              </div>
            ) : (
              <Button
                variant="success"
                size="default"
                onClick={onImportSampleTasks}
                loading={isImporting}
                iconName="Download"
              >
                Import Sample Tasks
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-primary/5 rounded-xl border border-primary/20 p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-foreground">
              <strong>Pro Tip:</strong> The tour takes about 2 minutes and will show you all the key features.
              You can skip or restart it anytime from the settings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeCard;